import { Link } from 'react-router-dom';
import { ArrowRight, Phone } from 'lucide-react';
import Seo, { faqJsonLd } from '@/components/Seo';
import { Reveal } from '@/components/motion';
import Faq from '@/components/Faq';
import { CtaSection } from '@/components/PageHero';
import { CONTACT, PROCESS_STEPS } from '@/content/site';

const STAGE_DETAILS = [
  {
    t: 'Understand your business',
    d: 'Goals, challenges, current arrangements, and priorities. Before anything else, the advisor needs to understand how your organization actually works—what you are trying to achieve, what is getting in the way, and what arrangements already exist.',
  },
  {
    t: 'Evaluate the opportunity',
    d: 'Review the relevant information and identify options worth considering. Not every opportunity deserves pursuit. The evaluation separates what is real from what is noise, and gives you a clear view of what could change.',
  },
  {
    t: 'Coordinate the solution',
    d: 'Explain recommendations, commercial terms, and implementation requirements before proceeding. You will know what is proposed, what it costs, and what it takes to implement—before you commit to anything.',
  },
  {
    t: 'Review progress',
    d: 'Assess outcomes and identify appropriate next steps. Engagements end with a clear-eyed review: what was achieved, what remains, and what is worth doing next.',
  },
];

const FAQS = [
  {
    q: 'How do we get started?',
    a: 'With a conversation. Call or send a note through the contact form describing your business and the area you want to improve. The first conversation is a consultation—no obligation, and no proposal before understanding.',
  },
  {
    q: 'What information will I need to provide?',
    a: 'It depends on the service area, but typically whatever already exists: contracts, invoices, or summaries of current arrangements. You do not need to prepare anything special for the first call. Please do not include patient information, financial account details, or other sensitive records in website inquiries.',
  },
  {
    q: 'What size companies do you work with?',
    a: 'The approach was built to flex: small businesses, growing companies, and large organizations have all been part of four decades of work. The questions are the same; the arrangements differ.',
  },
  {
    q: 'We already have providers and vendors—is that a problem?',
    a: 'Not at all. Many engagements evaluate existing arrangements rather than replace them. Where current providers deserve the business, the recommendation says so.',
  },
  {
    q: 'How does compensation work?',
    a: 'Certain cost-reduction opportunities are available on a contingency basis. Other services may involve subscription, project, or service fees. Applicable terms are reviewed before an engagement begins.',
  },
];

export default function OurApproach() {
  return (
    <>
      <Seo
        title="Our Approach — A Practical Advisory Process | KTK Advisors"
        description="A disciplined four-stage advisory process: Understand, Evaluate, Coordinate, Review. Personal guidance from an experienced consultant, with terms explained upfront."
        path="/our-approach"
        jsonLd={faqJsonLd(FAQS)}
      />

      <section className="relative overflow-hidden bg-deep" data-testid="approach-hero">
        <div className="aura aura--emerald" style={{ width: 620, height: 620, top: -240, left: -160 }} />
        <div className="wrap pt-40 pb-20 lg:pt-48 lg:pb-24 relative">
          <Reveal>
            <p className="eyebrow-mono">Our Approach</p>
            <h1 className="h1 mt-7 max-w-[24ch]">
              A clear, personal process from first call to <span className="gold-italic">honest review.</span>
            </h1>
            <p className="lede mt-8 max-w-[58ch]">
              You will always know where things stand, because the process is
              deliberate—and you deal with one advisor from start to finish.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-deep section" data-testid="approach-stages">
        <div className="wrap grid lg:grid-cols-[1fr_2fr] gap-14">
          <Reveal>
            <div className="lg:sticky lg:top-32">
              <p className="eyebrow-mono">Four Stages</p>
              <h2 className="h2 mt-6 max-w-[14ch]">
                Understand. Evaluate. Coordinate. Review.
              </h2>
              <p className="body-copy text-mist mt-6 max-w-[44ch]">
                The same discipline that anchors Six Sigma thinking—applied
                conversationally, without the jargon.
              </p>
              <p className="font-display italic text-[28px] text-goldbright mt-10">{CONTACT.founder}</p>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.26em] text-mist mt-2">
                {CONTACT.founderTitle}, KTK Advisors
              </p>
            </div>
          </Reveal>
          <div className="relative">
            <div className="absolute left-[21px] top-4 bottom-4 w-px bg-gold/25" aria-hidden="true" />
            {STAGE_DETAILS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="relative flex gap-8 items-start py-8">
                  <div className="tl-node">{PROCESS_STEPS[i].n}</div>
                  <div className="glass p-8 flex-1">
                    <h3 className="h3">{s.t}</h3>
                    <p className="mt-4 text-[15.5px] leading-relaxed text-mist">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card border-y border-gold/15 section" data-testid="approach-engagement">
        <div className="wrap grid lg:grid-cols-[1fr_2fr] gap-12 items-start">
          <Reveal>
            <p className="eyebrow-mono">Engagement Terms</p>
            <h2 className="h2 mt-6 max-w-[16ch]">Clear before you commit.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass p-10 lg:p-12 relative overflow-hidden">
              <span className="font-display text-[90px] leading-none absolute top-2 right-8 text-goldbright/15 select-none" aria-hidden="true">"</span>
              <p className="lede !text-ivory/90 max-w-[54ch]">
                Certain cost-reduction opportunities are available on a
                contingency basis. Other services may involve subscription,
                project, or service fees. Applicable terms are reviewed before an
                engagement begins.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-deep section" data-testid="approach-personal">
        <div className="wrap grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow-mono">One Advisor</p>
            <h2 className="h2 mt-6 max-w-[20ch]">The relationship is the service.</h2>
            <p className="body-copy text-mist mt-6 max-w-[52ch]">
              Large firms hand you to a junior. KTK Advisors is founder-led: the
              person who assesses your situation is the person who coordinates
              the work and reviews the results. When you have a question, you ask
              the decision-maker.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-7">
              <Link to="/contact" data-testid="approach-cta-link" className="btn btn-gold">
                Schedule a Consultation
                <ArrowRight size={17} />
              </Link>
              <a
                href={CONTACT.phoneHref}
                data-testid="approach-phone-link"
                className="inline-flex items-center gap-2 text-[15px] font-semibold text-ivory/85 hover:text-goldbright transition-colors"
              >
                <Phone size={15} />
                {CONTACT.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-4">
              {PROCESS_STEPS.map((s) => (
                <div key={s.n} className="glass p-7">
                  <span className="font-mono text-[11px] text-goldbright">{s.n}</span>
                  <h3 className="h3 mt-3 !text-[18px]">{s.title}</h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-mist">{s.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-card border-y border-gold/15 section" data-testid="approach-faqs">
        <div className="wrap grid lg:grid-cols-[1fr_1.6fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">Common Questions</p>
            <h2 className="h2 mt-6 max-w-[16ch]">Before you reach out.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Faq items={FAQS} baseId="approach-faq" />
          </Reveal>
        </div>
      </section>

      <CtaSection
        title="Start with a conversation."
        copy="No proposal before understanding. Tell us about your business and we'll take it from there."
        buttonLabel="Schedule a Consultation"
        buttonTo="/contact"
        testid="approach-cta-button"
      />
    </>
  );
}
